import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { JSONContent } from '@tiptap/react'
import { EditorContent } from '@tiptap/react'
import {
  LuBold,
  LuItalic,
  LuLink,
  LuList,
  LuListOrdered,
  LuStrikethrough,
  LuUnderline,
} from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import Popover from '@/components/common/PopoverV2'
import { parseJsonContent } from '@/components/common/TextArea/RichTextArea/lib'
import { LinkEditorPanel } from '@/components/common/TextArea/RichTextArea/menus/components/LinkEditorPanel'
import {
  ToolbarButton,
  TooltipWithShortcut,
} from '@/components/pursuits/v2/dashboard/comments/components'
import { useCommentEditor } from '@/components/pursuits/v2/dashboard/comments/hooks'
import {
  BLANK_CONTENT,
  COMMENT_TYPOGRAPHY_CLASSNAME,
  hasEditorContent,
} from '@/components/pursuits/v2/dashboard/comments/lib'

import { getModifierKey } from '@/helpers/platform'

interface ActivityDetailsEditorProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
  editorContentClassName?: string
  toolbarPosition?: 'top' | 'bottom'
  showToolbar?: boolean
  disabled?: boolean
}

export interface ActivityDetailsEditorRef {
  getJson: () => JSONContent
  setContent: (content: string | JSONContent) => void
  clear: () => void
  hasContent: () => boolean
}

/**
 * Clean, polished rich text editor for activity details.
 * Uses the same editor configuration as the pursuit comments editor.
 */
export const ActivityDetailsEditor = forwardRef<
  ActivityDetailsEditorRef,
  ActivityDetailsEditorProps
>(
  (
    {
      value,
      onChange,
      placeholder = 'Add notes, key points, or additional context...',
      className,
      editorContentClassName,
      toolbarPosition = 'top',
      showToolbar = true,
      disabled = false,
    },
    ref,
  ) => {
    const [containerIsFocused, setContainerIsFocused] = useState(false)
    const [content, setContent] = useState<JSONContent>(BLANK_CONTENT)
    const containerRef = useRef<HTMLDivElement>(null)

    // Check if editor has meaningful content
    const hasContent = useMemo(() => hasEditorContent(content), [content])

    const handleEditorUpdate = useCallback(
      ({ editor }: { editor: any }) => {
        const json = editor.getJSON()
        setContent(json)
        onChange?.(JSON.stringify(json))
      },
      [onChange],
    )

    const handleContainerBlur = useCallback(
      (event: React.FocusEvent<HTMLDivElement>) => {
        if (containerRef.current?.contains(event.relatedTarget as Node)) return
        setContainerIsFocused(false)
      },
      [],
    )

    const handleContainerFocus = useCallback(() => {
      setContainerIsFocused(true)
    }, [])

    const editor = useCommentEditor({
      content: value ?? BLANK_CONTENT,
      placeholder,
      onUpdate: handleEditorUpdate,
      editable: !disabled,
    })

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        getJson: () => editor?.getJSON() ?? BLANK_CONTENT,
        setContent: (newContent: string | JSONContent) => {
          const parsed =
            typeof newContent === 'string'
              ? (parseJsonContent(newContent) ?? BLANK_CONTENT)
              : newContent || BLANK_CONTENT
          editor?.commands.setContent(parsed)
          setContent(parsed)
        },
        clear: () => {
          editor?.commands.setContent(BLANK_CONTENT)
          setContent(BLANK_CONTENT)
        },
        hasContent: () => hasContent,
      }),
      [editor, hasContent],
    )

    const handleBoldClick = useCallback(() => {
      editor?.chain().focus().toggleBold().run()
    }, [editor])

    const handleItalicClick = useCallback(() => {
      editor?.chain().focus().toggleItalic().run()
    }, [editor])

    const handleUnderlineClick = useCallback(() => {
      editor?.chain().focus().toggleUnderline().run()
    }, [editor])

    const handleStrikeClick = useCallback(() => {
      editor?.chain().focus().toggleStrike().run()
    }, [editor])

    const handleBulletListClick = useCallback(() => {
      editor?.chain().focus().toggleBulletList().run()
    }, [editor])

    const handleOrderedListClick = useCallback(() => {
      editor?.chain().focus().toggleOrderedList().run()
    }, [editor])

    const handleSetLink = useCallback(
      (url: string, openInNewTab?: boolean) => {
        editor
          ?.chain()
          .focus()
          .setLink({ href: url, target: openInNewTab ? '_blank' : undefined })
          .run()
      },
      [editor],
    )

    const handleClickParent = useCallback(() => {
      editor?.commands.focus()
    }, [editor])

    const modifierKey = getModifierKey()

    if (!editor) return null

    const toolbar = (
      <div
        className={twMerge(
          'flex items-center',
          toolbarPosition === 'top' ? 'border-b' : 'border-t',
          'bg-primary-50 dark:bg-primary-900',
          'px-2.5 py-1',
          containerIsFocused
            ? 'border-primary-200 dark:border-primary-700'
            : 'border-primary-100 dark:border-primary-800',
        )}
      >
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={handleBoldClick}
            isActive={editor.isActive('bold')}
            tooltip={
              <TooltipWithShortcut label="Bold" shortcut={`${modifierKey}B`} />
            }
          >
            <LuBold size={16} />
          </ToolbarButton>

          <ToolbarButton
            onClick={handleItalicClick}
            isActive={editor.isActive('italic')}
            tooltip={
              <TooltipWithShortcut
                label="Italic"
                shortcut={`${modifierKey}I`}
              />
            }
          >
            <LuItalic size={16} />
          </ToolbarButton>

          <ToolbarButton
            onClick={handleUnderlineClick}
            isActive={editor.isActive('underline')}
            tooltip={
              <TooltipWithShortcut
                label="Underline"
                shortcut={`${modifierKey}U`}
              />
            }
          >
            <LuUnderline size={16} />
          </ToolbarButton>

          <ToolbarButton
            onClick={handleStrikeClick}
            isActive={editor.isActive('strike')}
            tooltip={
              <TooltipWithShortcut
                label="Strikethrough"
                shortcut={`${modifierKey}Shift+S`}
              />
            }
          >
            <LuStrikethrough size={16} />
          </ToolbarButton>

          <div className="w-px h-4 bg-primary-200 dark:bg-primary-700 mx-1" />

          <ToolbarButton
            onClick={handleBulletListClick}
            isActive={editor.isActive('bulletList')}
            tooltip={
              <TooltipWithShortcut
                label="Bullet list"
                shortcut={`${modifierKey}Shift+8`}
              />
            }
          >
            <LuList size={16} />
          </ToolbarButton>

          <ToolbarButton
            onClick={handleOrderedListClick}
            isActive={editor.isActive('orderedList')}
            tooltip={
              <TooltipWithShortcut
                label="Numbered list"
                shortcut={`${modifierKey}Shift+7`}
              />
            }
          >
            <LuListOrdered size={16} />
          </ToolbarButton>

          <div className="w-px h-4 bg-primary-200 dark:bg-primary-700 mx-1" />

          <Popover>
            <Popover.Trigger asChild>
              <ToolbarButton
                isActive={editor.isActive('link')}
                tooltip={<TooltipWithShortcut label="Insert link" />}
              >
                <LuLink size={16} />
              </ToolbarButton>
            </Popover.Trigger>
            <Popover.Content className="p-2">
              <LinkEditorPanel onSetLink={handleSetLink} />
            </Popover.Content>
          </Popover>
        </div>
      </div>
    )

    const editorContent = (
      <EditorContent
        editor={editor}
        className={twMerge(
          'min-w-full w-full',
          COMMENT_TYPOGRAPHY_CLASSNAME,
          '[&_.ProseMirror]:outline-none',
          '[&_.is-editor-empty:first-child::before]:text-primary-400 dark:[&_.is-editor-empty:first-child::before]:text-primary-500',
          '[&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.is-editor-empty:first-child::before]:float-left [&_.is-editor-empty:first-child::before]:h-0 [&_.is-editor-empty:first-child::before]:pointer-events-none',
          'focus:outline-none overflow-y-auto cursor-text',
          'px-3.5 py-2.5',
          'min-h-[200px]',
          editorContentClassName,
        )}
        onClick={handleClickParent}
      />
    )

    return (
      <div
        ref={containerRef}
        className={twMerge(
          'group/editor w-full rounded-lg overflow-hidden',
          'border transition-all duration-50',
          'bg-white dark:bg-default-dark',
          containerIsFocused && !disabled
            ? 'border-accent-400 dark:border-accent-500 ring-2 ring-accent-100 dark:ring-accent-900/30'
            : 'border-primary-200 dark:border-primary-800 hover:border-primary-300 dark:hover:border-primary-600',
          className,
        )}
        tabIndex={0}
        onBlur={handleContainerBlur}
        onFocus={handleContainerFocus}
      >
        {disabled || !showToolbar ? (
          editorContent
        ) : toolbarPosition === 'top' ? (
          <>
            {toolbar}
            {editorContent}
          </>
        ) : (
          <>
            {editorContent}
            {toolbar}
          </>
        )}
      </div>
    )
  },
)

ActivityDetailsEditor.displayName = 'ActivityDetailsEditor'
